import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { format, formatDistance } from 'date-fns'

import { EventContentArg } from '@fullcalendar/react'

import Checkbox from '../../UI/Inputs/Checkbox'
import CustomPopup from '../../UI/Popup'

import './Events.scss'
import CustomButton from '../../UI/Buttons/CustomButton'

import { useDispatch, useSelector } from '../../../store'
import ModalLabel from '../ModalLabel'
import {
  ATTACHMENT_TYPE,
  POST_SOCIAL_STATUS,
  POST_STATUS,
  SOCIAL_NETWORK,
  TPostAttachment
} from '../../../models/post'
import {
  APPROVED,
  definePostSocialStatus
} from '../../../models/postStatuses'
import {
  TExtendedPostAttachment
} from '../../../store/managePost/newTypes'
import VideoPreview from '../../UI/VideoPreview'
import Popup from 'reactjs-popup'
import { OriginalSocialIcons } from '../../UI/Icons/socials'
import QuestionIcon from '../../UI/Icons/Question'
import LoadableImage, { ASC_PREVIEW_SIZE, LOADER_STYLE } from '../../UI/LoadableImage'
import { APPROVAL_LIST_TYPE, HEADER_VIEW } from '../../../store/application/types'
import { isFulfilled, isRejected } from '@reduxjs/toolkit'
import { TOAST_MESSAGE } from '../../UI/ToastNotification/ToastNotification'
import { useToasts } from 'react-toast-notifications'
import { selectSocialMediaDataBySocialName } from '../../../selectors/ProjectSelector'
import { setPostToApproveAction } from '../../../store/managePost/slice'
import { setHeaderView, showApprovalListAction } from '../../../store/application/slice'

type ViewEventProps = {
  eventContent: EventContentArg
  selectedSchedules: any
  calendarDataRefs: any
  dragElementId: any
  setSelectedEvent: any
  project: any,
  isSinglePost?: boolean,
  approveCurrentRound:any,
  requireApproverRevision:any,
  setCurrectPostToApproval:any,
  fetchEvents:any,
  setSelectedSchedules:any
}

const ViewEvent: FC<ViewEventProps> = ({
  eventContent,
  selectedSchedules,
  calendarDataRefs,
  dragElementId,
  setSelectedEvent,
  project,
  isSinglePost,
  approveCurrentRound,
  requireApproverRevision,
  setCurrectPostToApproval,
  fetchEvents,
  setSelectedSchedules,
  ...rest
}) => {
  const { event, isMirror } = eventContent
  const { isShowPopup,startDate, endDate} = useSelector((state) => ({
    isShowPopup: state.app.isShowPopup,
    startDate:state.calendarApproval.startDate,
    endDate:state.calendarApproval.endDate,
  }))
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const checked: boolean = !!selectedSchedules.find(
    (selectedEvt: any) => selectedEvt.id === Number(event.id)
  )

  const {  user, loaders, approvalListType} = useSelector((state) => ({
    user: state.auth.user,
    loaders: state.managePost.loaders,
    approvalListType: state.app.approvalListType
  }))

  const { schedule, post } = useMemo(
    () => ({
      post: event.extendedProps?.schedule?.post,
      schedule: event.extendedProps?.schedule
    }),
    [event]
  )


  useEffect(() => {
    dispatch(showApprovalListAction(APPROVAL_LIST_TYPE.PENDING))
  }, [])

  const approveAndMoveToNextRound = async (id: number) => {
    const approveCurrentRoundResponse = await approveCurrentRound({ post_social_id: id })
    if (isFulfilled(approveCurrentRoundResponse)) {
      fetchEvents({ start:startDate,end: endDate, id: project.id })
      setSelectedSchedules([])
      addToast(TOAST_MESSAGE.POST_APPROVED, { appearance: 'success' })
    } else if (isRejected(approveCurrentRoundResponse)) {
      addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
    }
  }

  const requestRevision = async (post: any,is_approved?:boolean) => {
    requireApproverRevision && requireApproverRevision({ postId: post.id, activeSocial:post.post_socials[0].social_network,is_approved })
    setCurrectPostToApproval(post)
  }


  const isApprovingPostSocial = useCallback(
    (postId: number) => {
      return loaders.post.approve && loaders.post.processingIds.includes(postId)
    },
    [loaders.post.approve, loaders.post.processingIds]
  )

  const defineTypeMediaIsVideo = (attachment: TPostAttachment) =>
    attachment?.object_type === ATTACHMENT_TYPE.TYPE_VIDEO


  const renderEvent = () => {
    if (!post.post_socials.length) return null

    return (
      <div
        className='calendar-event'
        key={`calendar-event-post-${post.id}`}
        ref={(el) => {
          if (el) calendarDataRefs.current.setEventToCell(event, el?.parentNode)
        }}
      >

        {/*<Checkbox*/}
        {/*  type='checkbox'*/}
        {/*  checked={checked}*/}
        {/*  onChange={() => {*/}
        {/*    setSelectedEvent(event)*/}
        {/*  }}*/}
        {/*/>*/}

        <div className='calendar-event__time'>
          {format(event.start as Date, 'h:mm a').toLowerCase()}
        </div>
        <div className='calendar-event__type'>{schedule.type}</div>
        <div className='post-data-wrapper'>
          {post.post_socials.map((socialPost: any, i: number) => (
            <CustomPopup
              key={`post-popup-${i}`}
              keepTooltipInside='body'
              isShowPopup={isShowPopup}
              trigger={renderPostItem(socialPost)}
            >
              {eventPopup({ socialPost, schedule })}
            </CustomPopup>
          ))}
        </div>
        {isMirror && selectedSchedules.length > 1 && (
          <div className='calendar-event__quantity'>{selectedSchedules.length}</div>
        )}
      </div>
    )
  }


  const eventPopup = ({ socialPost, schedule }: any) => {
    const media = socialPost.attachments[0] as TExtendedPostAttachment
    const status = socialPost.status
    const { master_social_post_id  } = schedule.post


    const displayStatus = socialPost.status === POST_STATUS.STATUS_PENDING_PUBLISH ?
      'Pending manual publish' : socialPost.status.charAt(0).toUpperCase() +
      socialPost.status.replaceAll('_', ' ').slice(1)


    return (
      <div className={`event-content status-${status}`}>
        <div className='event-content__time'>
          {format(new Date(schedule.date + 'T' + schedule.time), 'h:mm a')}
        </div>
        <div className='event-content__type'>
          {schedule.post.type.charAt(0).toUpperCase() +
            schedule.post.type.replace('_', ' ').slice(1)}
        </div>
        <div className='event-content__post-data'>
          <div className='event-content__post-data__preview'>
            <div className={`event-content__post-data__preview__badge`}>
              {OriginalSocialIcons[socialPost.social_network as SOCIAL_NETWORK]}
            </div>
            {defineTypeMediaIsVideo(media) ? (
              <VideoPreview
                attachment={media}
                width={130}
                className='event-content__post-data__preview__video'
              />
            ) : (
              <LoadableImage
                media={media}
                containerWidth={130}
                loaderStyle={LOADER_STYLE.LIGHT}
                attachmentSize={ASC_PREVIEW_SIZE.MEDIUM}
              />
            )}
          </div>
          <div className='post-data'>
            <div className='post-data__text-wrapper'>
              <div className='post-data__text-wrapper__socials'>
                <div className='post-data__text-wrapper__socials__name'>
                  {socialPost.social_network}
                </div>
                <div className='post-data__text-wrapper__socials__status'>
                  &#9679;{' '}
                  {displayStatus}
                  {socialPost.status === POST_STATUS.STATUS_PUBLISHING_FAILED && (
                    <Popup
                      className='failed-message'
                      on='hover'
                      position='left center'
                      arrow={false}
                      trigger={() => (
                        <div>
                          <QuestionIcon />
                        </div>
                      )}
                    >
                      <div className='failed-message'>
                        {!!socialPost.last_social_api_response ? JSON.stringify(socialPost.last_social_api_response) : 'An internal error has occurred.'}
                      </div>
                    </Popup>
                  )}
                </div>
              </div>
              <div className='post-data__text-wrapper__short-descr'>{socialPost.message}</div>
            </div>
            <div className='post-data__date-wrapper'>
              <div className='post-data__date-wrapper__date'>
                <div className='post-data__date-wrapper__date__title'>Publish date</div>
                <div className='post-data__date-wrapper__date__date'>
                  {format(new Date(schedule.date + 'T' + schedule.time), 'dd/MM/yy h:mm a')}
                </div>
              </div>
              <div className='post-data__date-wrapper__date'>
                <div className='post-data__date-wrapper__date__title'>Last update</div>
                <div className='post-data__date-wrapper__date__date'>
                  {formatDistance(new Date(socialPost.updated_at), new Date())} ago
                </div>
              </div>
            </div>
          </div>
        </div>

        {status !==
        POST_SOCIAL_STATUS.STATUS_REQUIRES_REVISION &&
        (!master_social_post_id ||
          socialPost?.id === master_social_post_id) && (
          <div className='event-content__actions event-content__actions-reset'>
            {!isSinglePost && requireApproverRevision && !(APPROVED === definePostSocialStatus(socialPost.status, user?.agency_role) || socialPost?.is_approved) && !socialPost?.is_approved && !!post.can_approve_post_socials.includes(socialPost?.id) && (
              <CustomButton onClick={() => requestRevision(post,socialPost?.is_approved)} button='secondary'>
                Request revision
              </CustomButton>
            )}

            {!(APPROVED === definePostSocialStatus(socialPost.status, user?.agency_role) || socialPost?.is_approved) && !socialPost?.is_approved && !!post.can_approve_post_socials.includes(socialPost?.id) && (
              <CustomButton
                onClick={() => approveAndMoveToNextRound(socialPost.id)}
                inProgress={isApprovingPostSocial(socialPost.id)}
                button='primary'
              >
                Approve
              </CustomButton>
            )}

            {!isSinglePost && (APPROVED === definePostSocialStatus(socialPost.status, user?.agency_role) || socialPost?.is_approved) && socialPost?.status!=='live' && socialPost?.status!=='publishing_failed' && socialPost?.is_approved && !!post.can_unapprove_post_socials.includes(socialPost?.id) &&  (
              <CustomButton
                button='secondary'
                onClick={() => requestRevision(post,socialPost?.is_approved)}
                inProgress={loaders.post.approve}
              >
                Unapprove and request revision
              </CustomButton>
            )}

          </div>
        )}
      </div>
    )
  }

  const renderPostItem = ({ attachments, social_network, status }: any) => {
    const media: TExtendedPostAttachment = attachments[0]
    return (
      <div className={`post-data status-${status}`}>
        <div className='post-data__post-wrapper'>
          <div className='post-data__post-wrapper__preview'>
            <LoadableImage
              media={media}
              loaderStyle={LOADER_STYLE.LIGHT}
              attachmentSize={ASC_PREVIEW_SIZE.SMALL}
            />
          </div>
          <div className={`post-data__post-wrapper__social-icon`}>
            {OriginalSocialIcons[social_network as SOCIAL_NETWORK]}
          </div>
          <div className='post-data__post-wrapper__social-name'>{social_network}</div>
        </div>
      </div>
    )
  }

  const renderLabel = () => {
    const data = {
      label: event,
      project: { id: event.extendedProps.project_id },
      date_start: event.start as Date,
      date_end: event.end || event.start as Date,
      calendarDataRefs
    }
    return <ModalLabel {...data} />
  }
  return event.extendedProps.isPost ? renderEvent() : renderLabel()
}

export default ViewEvent
